import { useState, useEffect, useMemo } from "react";
import Wrapper from "../../../components/helpers/Wrapper";
import PageHeader from "../../../components/layout/pageHeader";
import SelectedGlobalFilter from "../../../components/ui/SelectedGlobalFilter";
import Card from "../../../components/ui/Card";
import Slider from "../../../components/ui/Slider";
import { SwiperSlide } from "swiper/react";
import { createLoaderSection, createErrorLoaderSection, createNoDataLoaderSection } from "../../../components/ui/states/States";
import WrapperCard from "../../../components/ui/card/card";
import useApi from "../../../hooks/useApi";
import NoDataAvailableChecker from "../../../utils/NoDataAvailableChecker";
import ErrorBoundary from "../../../utils/ErrorBoundary";
import ExportDropdown from "../../../components/ui/dropdown/ExportDropdown";
import ServerErrorsLoader from "../../../components/ui/loaders/ServerErrosLoader";
import SimpleTreeMap from "../../../components/ui/charts/SimpleTreeMap";
import WaterFallChart from "../../../components/ui/charts/WaterFallChart";
import Loader from "../../../components/ui/loaders/Loader";
import NoDataAvailableLoader from "../../../components/ui/loaders/NoDataAvailable";
// import { useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import useFetch from "../../../hooks/useFetch";
// import SummaryDatatable from "../../../components/ui/tables/SummaryDatatable";
import NestedSummaryTable from "../../../components/ui/tables/NestedSummaryTable";
import MapComponent from '../../../pageComponents/MapComponent';


const filterKeyMap = {
    country: "country_id",
    store_type: "store_type_id",
    category: "category_id",
    sub_category: "sub_category_id",
    segment: "segment_id",
    form: "form_id",
    manufacture: "manufacture_id",
    brand: "brand_id",
    variant: "variant_id",
    pack_size: "pack_size_id",
    promotion: "promotion_id",
    sku: "sku_id",
};

const SalesDeepDiveWithWaterfall = () => {
    const api_version = "-v1";
    const [breadcrumb, setBreadcrumb] = useState(["HOME"]);
    const [chart, setChart] = useState({ data: [], label: [] });
    const [isLoadingDeepDive, setIsLoadingDeepDive] = useState(false);
    const [errorDeepDive, setErrorDeepDive] = useState(null);

    const [waterfallChart, setWaterfallChart] = useState({ chartData: [], chartLabels: [] });
    const [isLoadingWaterfall, setIsLoadingWaterfall] = useState(false);
    const [errorWaterfall, setErrorWaterfall] = useState(null);

    const [waterfallFilter, setWaterfallFilter] = useState({});
    const { post } = useFetch();

    const levelKeys = [
        "country", "store_type", "category", "sub_category", "segment",
        "form", "manufacture", "brand", "variant", "pack_size",
        "promotion", "sku"
    ];
    const MAX_LEVEL = 12;

    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    const chart_filter = useMemo(
        () =>
            breadcrumb.slice(1).reduce((acc, label, idx) => {
                const key = levelKeys[idx];
                if (key && label) acc[key] = label;
                return acc;
            }, {}),
        [breadcrumb]
    );

    const salesPayload = useMemo(
        () => ({
            global_filter: {
                date_filter: {
                    mat_ty_start_date: startDate,
                    mat_ty_end_date: endDate,
                    mat_ly_start_date: prevStartDate,
                    mat_ly_end_date: prevEndDate,
                    mat_2y_start_date: secondPrevStartDate,
                    mat_2y_end_date: secondPrevEndDate,
                },
                filter: apiGlobalFilters
            },
            section_filter: {
                level: breadcrumb.length,
                chart_filter: Object.keys(chart_filter).length ? chart_filter : undefined
            }
        }),
        [
            startDate, endDate, prevStartDate, prevEndDate,
            secondPrevStartDate, secondPrevEndDate, breadcrumb, chart_filter, apiGlobalFilters
        ]
    );

    const waterfallPayload = useMemo(
        () => ({
            global_filter: {
                date_filter: {
                    mat_ty_start_date: startDate,
                    mat_ty_end_date: endDate,
                    mat_ly_start_date: prevStartDate,
                    mat_ly_end_date: prevEndDate,
                    mat_2y_start_date: secondPrevStartDate,
                    mat_2y_end_date: secondPrevEndDate,
                },
                filter: apiGlobalFilters
            },
            section_filter: {
                chart_filter: waterfallFilter,
            }
        }),
        [
            startDate, endDate, prevStartDate, prevEndDate,
            secondPrevStartDate, secondPrevEndDate, waterfallFilter, apiGlobalFilters
        ]
    );

    useEffect(() => {
        const newChartFilter = breadcrumb.slice(1).reduce((acc, lbl, idx) => {
            const key = levelKeys[idx];
            if (key && lbl) acc[key] = lbl;
            return acc;
        }, {});
        setWaterfallFilter(newChartFilter);
        // eslint-disable-next-line
    }, [breadcrumb]);

    useEffect(() => {
        setIsLoadingDeepDive(true);
        setErrorDeepDive(null);
        post(`ri/summary/summary-cause-analysis-deep-dive${api_version}`, salesPayload)
            .then(res => setChart({ data: res?.data || [], label: res?.label || [] }))
            .catch(err => setErrorDeepDive(err))
            .finally(() => setIsLoadingDeepDive(false));
        // eslint-disable-next-line
    }, [salesPayload]);

    useEffect(() => {
        setIsLoadingWaterfall(true);
        setErrorWaterfall(null);
        post(`ri/summary/summary-cause-analysis-waterfall${api_version}`, waterfallPayload)
            .then(res => setWaterfallChart(res || { chartData: [], chartLabels: [] }))
            .catch(err => setErrorWaterfall(err))
            .finally(() => setIsLoadingWaterfall(false));
        // eslint-disable-next-line
    }, [waterfallPayload]);

    const onDrill = label => {
        if (!label) return;
        if (breadcrumb.length >= MAX_LEVEL) {
            alert("No further drill down available.");
            return;
        }
        setBreadcrumb(bc => [...bc, label]);
    };

    const onCrumbClick = idx => {
        if (idx < breadcrumb.length - 1) {
            setBreadcrumb(breadcrumb.slice(0, idx + 1));
        }
    };

    return (
        <Wrapper>
            <div id="cause_analysis" className="section_card">
                <div className="section_card_header" style={{ marginBottom: 0 }}>
                    <div className="flex column align_start">
                        <div className="section_info">
                            <h2 className="section_card_title">Cause Analysis by Category & By Metric</h2>
                        </div>
                        <div className="treemap_breadcrumb" style={{
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            fontFamily: 'Ubuntu, sans-serif',
                            whiteSpace: 'nowrap',
                            gap: 0
                        }}>
                            {breadcrumb.map((crumb, idx) => (
                                <span key={idx} style={{ display: 'flex', alignItems: 'center' }}>
                                    {/* <span
                                        onClick={() => idx < breadcrumb.length - 1 && onCrumbClick(idx)}
                                        style={{
                                            color: idx === breadcrumb.length - 1 ? '#979797' : '#11A1FD',
                                            fontWeight: idx === 0 ? 600 : idx === breadcrumb.length - 1 ? 400 : 700,
                                            cursor: idx < breadcrumb.length - 1 ? 'pointer' : 'default',
                                            textDecoration: 'none',
                                            marginRight: 4
                                        }}
                                    > */}
                                    <span
                                        onClick={() => idx < breadcrumb.length - 1 && onCrumbClick(idx)}
                                        style={{
                                            color: idx === breadcrumb.length - 1 ? '#979797' : '#11A1FD',
                                            fontWeight: idx === breadcrumb.length - 1 ? 400 : 700,
                                            cursor: idx < breadcrumb.length - 1 ? 'pointer' : 'default',
                                            textDecoration: 'none',
                                            marginRight: 4,
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {crumb}
                                    </span>
                                    {idx < breadcrumb.length - 1 && (
                                        <span style={{ color: '#979797', margin: '0 4px', fontWeight: 400 }}>/</span>
                                    )}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div data-html2canvas-ignore={true}>
                        {/* <ExportDropdown
                            sectionId="cause_analysis"
                            sectionName="Cause Analysis"
                            sectionData={waterfallChart.chartData}
                        /> */}
                    </div>
                </div>
                {/* Add padding to section_card_body for clean side space */}
                <div
                    className="section_card_body"
                    style={{
                        padding: "0 32px",
                    }}
                >
                    <div
                        className="charts-side-by-side"
                        style={{
                            display: 'flex',
                            gap: 24,
                            alignItems: 'flex-start',
                            width: '100%',
                        }}
                    >
                        {/* Treemap Left */}
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                            <div className="section_card_chart_wrapper" style={{
                                background: "#fff",
                                borderRadius: 12,
                                padding: 0,
                                minHeight: 340,
                                display: "flex",
                                flexDirection: "column"
                            }}>
                                {isLoadingDeepDive && <Loader loaderType="multiLineChartLoader" />}
                                {errorDeepDive && (
                                    <div className="error-loader-section">
                                        <div className="error-message">Error loading data. Please try again.</div>
                                    </div>
                                )}
                                {!isLoadingDeepDive && !errorDeepDive && chart.data && chart.data.length > 0 && (
                                    <ErrorBoundary>
                                        <SimpleTreeMap
                                            chartId={"simpleTreemaptest"}
                                            chartClass="section-card-chart"
                                            chartData={chart.data}
                                            chartLabels={chart.label}
                                            colorChangeParameter="growth_percentage"
                                            positiveNegativeNeutral={true}
                                            levelHandler={node => {
                                                const label = node?.label || node?.name || "";
                                                onDrill(label);
                                            }}
                                        />
                                        {/* Legend under TreeMap */}
                                        {/* <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            marginTop: 18,
                                            gap: 32,
                                            flexWrap: 'wrap',
                                            fontSize: 12
                                        }}>
                                            <span style={{ color: "#31343B", marginRight: 24 }}>
                                                Area = Sales Value
                                            </span>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: '50%',
                                                    background: '#06a57d',
                                                    marginRight: 3,
                                                    // marginTop: 12,
                                                }} />
                                                <span style={{ color: "#31343B" }}>
                                                    Sales % Growth
                                                </span>
                                            </span>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: '50%',
                                                    background: '#b51c2a',
                                                    marginRight: 3,
                                                    // marginTop: 12,
                                                    
                                                }} />
                                                <span style={{ color: "#31343B" }}>
                                                    Sales % Degrowth
                                                </span>
                                            </span>
                                        </div> */}
                                    </ErrorBoundary>
                                )}
                            </div>
                        </div>
                        {/* Waterfall Right */}
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                            <div className="section_card_chart_wrapper" style={{
                                background: "#fff",
                                borderRadius: 12,
                                padding: 0,
                                minHeight: 340,
                                display: "flex",
                                flexDirection: "column"
                            }}>
                                {isLoadingWaterfall && <Loader loaderType="multiLineChartLoader" />}
                                {errorWaterfall && (
                                    <div className="error-loader-section">
                                        <div className="error-message">Error loading data. Please try again.</div>
                                    </div>
                                )}
                                {!isLoadingWaterfall && !errorWaterfall && waterfallChart.chartData && waterfallChart.chartData.length > 0 && (
                                    <ErrorBoundary>
                                        <WaterFallChart
                                            chartId="waterfallChart"
                                            chartClass="chart-container"
                                            chartLabels={waterfallChart.chartLabels}
                                            chartData={waterfallChart.chartData}
                                            height="350px"
                                        />
                                    </ErrorBoundary>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Wrapper>
    );
};

const SalesPerformance = () => {
    const api_version = "-v1";

    // 1. Get date values from Redux (same as TrendAnalysis)
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // 2. Use those dates and global filters in your payload
    const [payload, setPayload] = useState({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        },
    });

    // 3. Update payload when Redux date values or global filters change
    useEffect(() => {
        setPayload(prev => ({
            ...prev,
            global_filter: {
                ...prev.global_filter,
                date_filter: {
                    mat_ty_start_date: startDate,
                    mat_ty_end_date: endDate,
                    mat_ly_start_date: prevStartDate,
                    mat_ly_end_date: prevEndDate,
                    mat_2y_start_date: secondPrevStartDate,
                    mat_2y_end_date: secondPrevEndDate,
                },
                filter: apiGlobalFilters
            }
        }));
        // eslint-disable-next-line
    }, [
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters
    ]);

    const { post } = useFetch(); // Use your custom useFetch hook
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setIsLoading(true);
        post(`ri/sales/sales-sales-performance${api_version}`, payload)
            .then((response) => {
                setData(response?.data || response);
                setError(null);
            })
            .catch((err) => {
                setError(err);
                setData(null);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [payload, post, api_version]);

    let content;
    if (isLoading) {
        content = createLoaderSection('cardLoader', "Sales Performance", 'chartCard');
    } else if (error) {
        content = createErrorLoaderSection('cardType');
    } else if (!isLoading && (!data || data.length === 0 || NoDataAvailableChecker(data))) {
        content = createNoDataLoaderSection('cardType');
    } else {
        const CARD_DATA = data || [];
        content = (
            <Slider sliderTitle="Sales Performance">
                {CARD_DATA.map((data, index) => (
                    <SwiperSlide key={index}>
                        <Card
                            module="summary"
                            section="summary_distribution_performance"
                            variant={"chartCard"}
                            cardTitle={data.label}
                            currentValue={data.currentValue}
                            previousValue={data.previousValue}
                            chartType={"Line"}
                            chartData={data.subData?.data}
                            chartDataLabels={data.subData?.dimension}
                        />
                    </SwiperSlide>
                ))}
            </Slider>
        );
    }

    return (
        <Wrapper>
            <ErrorBoundary>
                {content}
            </ErrorBoundary>
        </Wrapper>
    );
};

const DistributionPerformance = () => {
    const api_version = "-v1";

    // 2. Get date values from Redux
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    // 3. Use those dates and global filters in your payload, and store in state
    const [payload, setPayload] = useState({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        }
        // section_filter: {
        //     value_dropdown: "sales_unit",
        //     top_bottom: {
        //         sort_by: "desc",
        //         size: 5
        //     }
        // }
    });

    // 4. Update payload when Redux date values or global filters change
    useEffect(() => {
        setPayload(prev => ({
            ...prev,
            global_filter: {
                ...prev.global_filter,
                date_filter: {
                    mat_ty_start_date: startDate,
                    mat_ty_end_date: endDate,
                    mat_ly_start_date: prevStartDate,
                    mat_ly_end_date: prevEndDate,
                    mat_2y_start_date: secondPrevStartDate,
                    mat_2y_end_date: secondPrevEndDate,
                },
                filter: apiGlobalFilters
            }
        }));
        // eslint-disable-next-line
    }, [
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate,
        apiGlobalFilters
    ]);

    const { post } = useFetch();
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // 5. Fetch data whenever payload changes
    useEffect(() => {
        setIsLoading(true);
        setError(null);

        post(`ri/distribution/distribution-distribution-performance${api_version}`, payload)
            .then(response => {
                setData(response?.data || response);
                setError(null);
            })
            .catch(err => {
                setError(err);
                setData(null);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [payload, post, api_version]);

    let content;

    if (isLoading) {
        content = createLoaderSection('cardLoader', "Distribution Performance", 'chartCard');
    } else if (error) {
        content = createErrorLoaderSection('cardType');
    } else if (!isLoading && (!data || data.length === 0 || NoDataAvailableChecker(data))) {
        content = createNoDataLoaderSection('cardType');
    } else {
        const CARD_DATA = data || [];
        content = (
            <Slider sliderTitle="Distribution Performance">
                {CARD_DATA.map((data, index) => (
                    <SwiperSlide key={index}>
                        <Card
                            module="summary"
                            section="summary_distribution_performance"
                            variant={"chartCard"}
                            cardTitle={data.label}
                            currentValue={data.currentValue}
                            previousValue={data.previousValue}
                            chartType={"Line"}
                            chartData={data.subData?.data}
                            chartDataLabels={data.subData?.dimension}
                        />
                    </SwiperSlide>
                ))}
            </Slider>
        );
    }

    return (
        <Wrapper>
            <ErrorBoundary>
                {content}
            </ErrorBoundary>
        </Wrapper>
    );
};



const TABLE_HEADERS = [
    { name: "Time Period", field: "time_period", is_display: true },
    { name: "Segment", field: "segment", is_display: true },
    { name: "Store Type", field: "store_type", is_display: true },
    { name: "Manufacture", field: "manufacture", is_display: true },
    { name: "Brand", field: "brand", is_display: true },
    { name: "Variant", field: "variant", is_display: true },
    { name: "Pack Size", field: "pack_size", is_display: true },
    { name: "SKU", field: "sku", is_display: true },

    { name: "Sales Value", field: "sales_value", growth_field: "sales_value_growth", is_display: true },
    { name: "Alert Cause", field: "sales_alert_status", is_display: true },
    { name: "Price Per Volume", field: "ppl", growth_field: "ppl_growth", is_display: true },
    { name: "Sales Volume", field: "sales_volume", growth_field: "sales_volume_growth", is_display: true },

    { name: "Sales Value per NumDTotal (No.of Store %)", field: "sales_value_per_numdtotal", is_display: true },
    { name: "NumDTotal (No. of Store %)/Sales Volume", field: "numdtotal_no_of_store_per_sales_volume", is_display: true },
    { name: "NumDTotal(No. of Store %)", field: "numdtotal_no_of_store", is_display: true },
    { name: "Sales Volume per SKU", field: "sales_volume_per_sku", is_display: true },
    { name: "No. of Brand per NumDTotal (No. of Store %)", field: "no_of_brand_per_store", is_display: true },
    { name: "No. of SKU per brand", field: "no_of_sku_per_brand", is_display: true },

    { name: "Weighted Distribution Points (TDP)", field: "tdp", growth_field: "tdp_growth", is_display: true },
    { name: "Numeric Distribution Selling", field: "numdtotal", growth_field: "numdtotal_growth", is_display: true },

    { name: "Market Share By Sales Value", field: "market_share_by_sales_value", growth_field: "market_share_by_sales_value_growth", is_display: true },
    { name: "Market Share By Sales Volume", field: "market_share_by_sales_volume", growth_field: "market_share_by_sales_volume_growth", is_display: true },
    { name: "Premium Index", field: "premium_index", is_display: true },

    { name: "Promotional Sales Value", field: "promotional_sales_value_raw", growth_field: "promotional_sales_value_raw_growth", is_display: true },
    { name: "Promotional Sales Value %", field: "promotional_sales_value", growth_field: "promotional_sales_value_growth", is_display: true },
    { name: "Sales Lag Promotional %", field: "lag_promotional_sales_value", is_display: true },

    { name: "Promotional PPL", field: "promotional_ppl", growth_field: "promotional_ppl_growth", is_display: true },

    { name: "Promotional Sales Volume", field: "promotional_sales_volume_raw", growth_field: "promotional_sales_volume_raw_growth", is_display: true },
    { name: "Promotional Sales Volume %", field: "promotional_sales_volume", growth_field: "promotional_sales_volume_growth", is_display: true },
    { name: "Sales Volume Lag Promotional %", field: "lag_promotional_sales_volume", is_display: true },

    { name: "Promotional TDP", field: "promotional_tdp_raw", growth_field: "promotional_tdp_raw_growth", is_display: true },
    { name: "Promotional TDP %", field: "promotional_tdp", growth_field: "promotional_tdp_growth", is_display: true },
    { name: "TDP Lag Promotional %", field: "lag_promotional_tdp", is_display: true },

    { name: "Sales Value Growth", field: "sales_value_growth", is_display: false },
    { name: "Sales Volume Growth", field: "sales_volume_growth", is_display: false },
    { name: "Price Per Volume Growth", field: "ppl_growth", is_display: false },
    { name: "Weighted Distribution Points (TDP) Growth", field: "tdp_growth", is_display: false },
    { name: "Numeric Distribution Selling Growth", field: "numdtotal_growth", is_display: false },
    { name: "Market Share By Sales Value Growth", field: "market_share_by_sales_value_growth", is_display: false },
    { name: "Market Share By Sales Volume Growth", field: "market_share_by_sales_volume_growth", is_display: false },
    { name: "Promotional Sales Value Growth", field: "promotional_sales_value_growth", is_display: false },
    { name: "Promotional Sales Volume Growth", field: "promotional_sales_volume_growth", is_display: false },

    { name: "Promotional Sales Volume Growth", field: "promotional_sales_volume_raw_growth", is_display: false },
    { name: "Promotional Sales Value Growth", field: "promotional_sales_value_raw_growth", is_display: false },
    { name: "Promotional TDP Growth", field: "promotional_tdp_growth", is_display: false },
    { name: "Promotional PPL Growth", field: "promotional_ppl_growth", is_display: false },
    { name: "Promotional TDP Growth", field: "promotional_tdp_raw_growth", is_display: false },
];

const GROUP_BY_FIELDS = [
    "segment",
    "store_type",
    "manufacture",
    "brand",
    "variant",
    "pack_size",
    "sku",
];

// Helper to remove all descendants of a parentKey from an object
function removeDescendants(obj, parentKey) {
    const newObj = {};
    Object.keys(obj).forEach(key => {
        if (!(key.startsWith(parentKey + "-"))) {
            newObj[key] = obj[key];
        }
    });
    return newObj;
}

const countAllRecords = (data, nestedData, expandedNestedRows) => {
    let total = data.length;
    Object.keys(expandedNestedRows).forEach(key => {
        if (expandedNestedRows[key] && nestedData[key]) {
            const nestedRows = Array.isArray(nestedData[key]) ? nestedData[key] : [];
            total += nestedRows.length;
        }
    });
    return total;
};

const NestedSummaryTables = () => {
    const [rawTableData, setRawTableData] = useState([]);
    const [paginationConfig, setPaginationConfig] = useState({
        page_num: 1,
        page_size: 20,
        search_value: "",
        order_by: "",
        order_type: "asc"
    });

    // --- Nested state ---
    const [expandedNestedRows, setExpandedNestedRows] = useState({});
    const [nestedData, setNestedData] = useState({});
    const [loadingNested, setLoadingNested] = useState({});
    const [nestedPagination, setNestedPagination] = useState({});

    const { post, isLoading, error } = useFetch();
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);

    // Get global filter from Redux and map to API keys
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);

    const payload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        },
        section_filter: {},
        pagination: {
            page_num: paginationConfig.page_num,
            page_size: paginationConfig.page_size
        }
    }), [
        startDate, endDate, prevStartDate, prevEndDate,
        secondPrevStartDate, secondPrevEndDate, apiGlobalFilters,
        paginationConfig.page_num, paginationConfig.page_size,
        paginationConfig.order_type
    ]);

    useEffect(() => {
        const fetchData = async () => {
            const response = await post(`ri/summary/summary-sales-driver-insight-v2`, payload);
            window.localStorage.removeItem('nestedTableClientSearch');
            if (response && Array.isArray(response.data)) {
                setNestedData(prev => ({ ...prev, [0]: [] }));
                setExpandedNestedRows({});
                const dataWithKeys = response.data.map((item, index) => ({
                    ...item,
                    key: `${index}`,
                    isNested: false
                }));
                setRawTableData(dataWithKeys);
            } else {
                setNestedData(prev => ({ ...prev, [0]: [] }));
                setRawTableData([]);
                setExpandedNestedRows({});

            }
        };
        fetchData();
    }, [payload]);

    const pageSize = paginationConfig.page_size || 10;
    const pageNum = paginationConfig.page_num || 1;
    const searchValue = paginationConfig.search_value || "";
    const orderBy = paginationConfig.order_by;
    const orderType = paginationConfig.order_type || "asc";

    let filteredData = rawTableData;
    if (searchValue) {
        filteredData = filteredData.filter(row =>
            TABLE_HEADERS.some(header =>
                String(row[header.field] || "")
                    .toLowerCase()
                    .includes(searchValue.toLowerCase())
            )
        );
    }

    if (orderBy) {
        filteredData = filteredData.slice().sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];
            if (typeof aValue === "number" && typeof bValue === "number") {
                return orderType === "asc" ? aValue - bValue : bValue - aValue;
            }
            return orderType === "asc"
                ? String(aValue).localeCompare(String(bValue))
                : String(bValue).localeCompare(String(aValue));
        });
    }

    const flattenRows = (rows, nestedData, expandedNestedRows, parentKeyPrefix = '', level = 0) => {
        const result = [];
        rows.forEach((row, rowIdx) => {
            const parentKey = parentKeyPrefix === '' ? `${rowIdx}` : `${parentKeyPrefix}-${rowIdx}`;
            result.push({
                ...row,
                __isParent: level === 1,
                __level: level,
                __parentKey: parentKey,
            });

            if (expandedNestedRows[parentKey] && nestedData[parentKey]) {
                const children = Array.isArray(nestedData[parentKey]) ? nestedData[parentKey] : [];
                result.push(
                    ...flattenRows(children, nestedData, expandedNestedRows, parentKey, level + 1)
                );
            }
        });
        return result;
    };

    // // Replace your calculatePagination function with this:
    // const calculatePagination = (filteredData, nestedData, expandedNestedRows, pageSize, pageNum) => {
    //     const flatRows = flattenRows(filteredData, nestedData, expandedNestedRows);
    //     const totalRecords = flatRows.length;
    //     const totalPages = Math.max(Math.ceil(totalRecords / pageSize), 1);
    //     const startIdx = (pageNum - 1) * pageSize;
    //     const endIdx = startIdx + pageSize;
    //     const paginatedData = flatRows.slice(startIdx, endIdx);

    //     return {
    //         paginatedData,
    //         totalRecords,
    //         totalPages,
    //         recordCount: paginatedData.length,
    //     };
    // };

    // const { paginatedData, totalRecords, totalPages, recordCount } = (
    //     filteredData,
    //     nestedData,
    //     expandedNestedRows,
    //     pageSize,
    //     pageNum
    // );

    const handleTableConfigs = (newConfig) => {
        // console.log("New Config:", newConfig);
        setPaginationConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    const handleExpandNestedRow = async (parentKey, level, parentRow, page_num = 1, page_size = 10) => {
        // console.log("Expanding/Collapsing Row:", parentKey);

        const isCurrentlyExpanded = !!expandedNestedRows[parentKey];

        if (isCurrentlyExpanded) {
            setExpandedNestedRows(prev => removeDescendants({ ...prev, [parentKey]: false }, parentKey));
            setNestedData(prev => removeDescendants(prev, parentKey));
            setLoadingNested(prev => removeDescendants(prev, parentKey));
            return;
        }

        setExpandedNestedRows(prev => ({ ...prev, [parentKey]: true }));
        setLoadingNested(prev => ({ ...prev, [parentKey]: true }));

        try {
            const payload = getPayloadForLevel(level, parentRow, parentKey, page_num, page_size);
            const response = await post(`ri/summary/summary-sales-driver-insight-v2`, payload);

            setLoadingNested(prev => ({ ...prev, [parentKey]: false }));

            if (response && Array.isArray(response.data)) {
                const nestedDataWithKeys = response.data.map((item, idx) => ({
                    ...item,
                    key: `${parentKey}-${idx}`,
                    isNested: true,
                    parentKey: parentKey,
                    level: level
                }));

                setNestedData(prev => ({
                    ...prev,
                    [parentKey]: nestedDataWithKeys
                }));
            } else {
                setNestedData(prev => ({ ...prev, [parentKey]: [] }));
            }
        } catch (error) {
            console.error("Error fetching nested data:", error);
            setLoadingNested(prev => ({ ...prev, [parentKey]: false }));
            setNestedData(prev => ({ ...prev, [parentKey]: [] }));
        }
    };

    const getPayloadForLevel = (level, parentRow, parentKey, page_num, page_size) => {
        const selectedValues = {};

        let timePeriod = parentRow.time_period;
        if (!timePeriod && parentKey) {
            const topLevelIdx = parentKey.split("-")[0];
            // const topRow =  paginatedData && paginatedData[topLevelIdx];
            // if (topRow && topRow.time_period) {
            //     timePeriod = topRow.time_period;
            // }
        }
        if (timePeriod) {
            selectedValues.time_period = timePeriod;
        }

        // for (let i = 0; i < level - 1; i++) {
        //     console.log("=============", i, level, GROUP_BY_FIELDS[i]);
        //     const field = GROUP_BY_FIELDS[i];
        //     if (parentRow[field]) {
        //         selectedValues[field] = parentRow[field];
        //     }
        // }
        for (let i = 0; i < level; i++) {
            const field = GROUP_BY_FIELDS[i];
            if (parentRow[field]) {
                selectedValues[field] = parentRow[field];
            }
        }

        return {
            global_filter: {
                date_filter: {
                    mat_ty_start_date: startDate,
                    mat_ty_end_date: endDate,
                    mat_ly_start_date: prevStartDate,
                    mat_ly_end_date: prevEndDate,
                    mat_2y_start_date: secondPrevStartDate,
                    mat_2y_end_date: secondPrevEndDate,
                },
                filter: apiGlobalFilters

            },
            section_filter: {
                // group_by_clause: GROUP_BY_FIELDS.slice(0, level),
                group_by_clause: GROUP_BY_FIELDS.slice(0, level + 1),
                filter_select: [selectedValues],
                time_period: timePeriod
            },
            pagination: {
                page_num,
                page_size
            }
        };
    };

    const handleNestedPaginationChange = (parentKey, level, parentRow, page_num, page_size) => {
        setNestedPagination(prev => ({
            ...prev,
            [parentKey]: { page_num, page_size }
        }));
        handleExpandNestedRow(parentKey, level, parentRow, page_num, page_size);
    };

    const createErrorLoaderSection = (type) => (
        <div className={`error-loader-section ${type}`}>
            <div className="error-message">Error loading data. Please try again.</div>
        </div>
    );

    const createNoDataLoaderSection = (type) => (
        <div className={`no-data-loader-section ${type}`}>
            <div className="no-data-message">No data available</div>
        </div>
    );

    const flatRows = flattenRows(
        filteredData,
        nestedData,
        expandedNestedRows
    );

    let tableContent = null;
    if (isLoading) {
        tableContent = <Loader loaderType="smallTableLoader" />;
    } else if (error) {
        tableContent = createErrorLoaderSection('smallTableLoader');
    } else if (!isLoading && (!rawTableData || rawTableData.length === 0)) {
        tableContent = createNoDataLoaderSection('smallTableLoader');
    } else {
        tableContent = (
            <NestedSummaryTable
                tableTitle={"Sales Value Driver Insight"}
                tableData={flatRows}
                tableHeader={TABLE_HEADERS}
                // tableConfigs={paginationConfig}
                // setTableConfigs={handleTableConfigs}
                // tablePaginationData={{
                //     page_num: pageNum,
                //     page_size: pageSize,
                //     total_pages: totalPages,
                //     total_records: totalRecords,
                //     //   record_end: Math.min(pageNum * pageSize, totalRecords)
                //     record_end: recordCount // Use actual record count instead of calculation

                // }}
                showPagination={true}
                searchPlaceHolder="Search here..."
                expandedNestedRows={expandedNestedRows}
                setExpandedNestedRows={setExpandedNestedRows}
                handleExpandNestedRow={handleExpandNestedRow}
                nestedData={nestedData}
                loadingNested={loadingNested}
                nestedPagination={nestedPagination}
                handleNestedPaginationChange={handleNestedPaginationChange}
            // topLevelRows={paginatedData}
            />
        );
    }

    return (
        <Wrapper>
            <ErrorBoundary>
                {tableContent}
            </ErrorBoundary>
        </Wrapper>
    );
};

const dummyGeoJson = {
    "type": "FeatureCollection",
    "features": [
        {
            "type": "Feature",
            "geometry": { "type": "Point", "coordinates": [55.2708, 25.2048] }, // Dubai
            "properties": {
                "name": "DUBAI",
                "market_share": 34,
                "sales_value": 2.75,
                "sales_value_growth": -20.14
            }
        },
    ]
};

const Summary = () => {
    // State to control map panel visibility
    const [showMapPanel, setShowMapPanel] = useState(false);
    const [mapGeoJson, setMapGeoJson] = useState(null);
    const [mapLoading, setMapLoading] = useState(false);
    const [mapError, setMapError] = useState(null);
    const [hoverData, setHoverData] = useState({});
    const [hoverLoading, setHoverLoading] = useState(false);
    const [hoverError, setHoverError] = useState(null);

    // Get date and global filters from Redux
    const {
        startDate,
        endDate,
        prevStartDate,
        prevEndDate,
        secondPrevStartDate,
        secondPrevEndDate
    } = useSelector(state => state.date);
    const selectedFilters = useSelector((state) => state.globalFilter.selectedFilters);
    const apiGlobalFilters = useMemo(() => {
        const apiFilters = {};
        Object.entries(selectedFilters).forEach(([key, values]) => {
            if (Array.isArray(values) && values.length > 0 && filterKeyMap[key]) {
                apiFilters[filterKeyMap[key]] = values;
            }
        });
        return apiFilters;
    }, [selectedFilters]);
    const mapPayload = useMemo(() => ({
        global_filter: {
            date_filter: {
                mat_ty_start_date: startDate,
                mat_ty_end_date: endDate,
                mat_ly_start_date: prevStartDate,
                mat_ly_end_date: prevEndDate,
                mat_2y_start_date: secondPrevStartDate,
                mat_2y_end_date: secondPrevEndDate,
            },
            filter: apiGlobalFilters
        }
    }), [startDate, endDate, prevStartDate, prevEndDate, secondPrevStartDate, secondPrevEndDate, apiGlobalFilters]);
    const { post } = useFetch();

    // Listen for custom event from sidebar map button
    useEffect(() => {
        const handler = () => setShowMapPanel(true);
        window.addEventListener('open-map-panel', handler);
        return () => window.removeEventListener('open-map-panel', handler);
    }, []);

    // Fetch map data and hover data when map panel opens
    useEffect(() => {
        if (!showMapPanel) return;
        setMapLoading(true);
        setMapError(null);
        setHoverLoading(true);
        setHoverError(null);
        // Fetch map coordinates
        post('ri/map-coordinates', mapPayload)
            .then(response => {
                // Convert response to GeoJSON
                const features = Array.isArray(response)
                    ? response.map(item => ({
                        type: 'Feature',
                        geometry: {
                            type: 'Point',
                            coordinates: [item.longitude, item.latitude]
                        },
                        properties: {
                            ...item,
                            name: item.country_name || item.country_code
                        }
                    }))
                    : [];
                setMapGeoJson({ type: 'FeatureCollection', features });

                // Prepare country_ids for hover API
                const countryIds = features.map(f => f.properties.country_id).filter(Boolean);
                if (countryIds.length > 0) {
                    // Call hover API for all countries
                    const hoverPayload = {
                        global_filter: {
                            date_filter: mapPayload.global_filter.date_filter,
                            filter: {
                                ...mapPayload.global_filter.filter,
                                country_id: countryIds
                            }
                        }
                    };
                    post('ri/map-hover', hoverPayload)
                        .then(hoverResponse => {
                            // Store hover data by country_id
                            if (Array.isArray(hoverResponse)) {
                                const hoverDataObj = {};
                                hoverResponse.forEach(d => {
                                    if (d.country_id) hoverDataObj[d.country_id] = d;
                                });
                                setHoverData(hoverDataObj);
                            }
                        })
                        .catch(err => {
                            setHoverError(err);
                        })
                        .finally(() => setHoverLoading(false));
                } else {
                    setHoverData({});
                    setHoverLoading(false);
                }
            })
            .catch(err => {
                setMapError(err);
                setMapGeoJson(null);
                setHoverLoading(false);
            })
            .finally(() => setMapLoading(false));
    }, [showMapPanel, post, mapPayload]);

    // Hover handler for map markers
    const handleMarkerHover = (properties) => {
        if (!properties || !properties.country_id) return;
        setHoverLoading(true);
        setHoverError(null);
        // Add country_id to filter for hover
        const hoverPayload = {
            global_filter: {
                date_filter: mapPayload.global_filter.date_filter,
                filter: {
                    ...mapPayload.global_filter.filter,
                    country_id: [properties.country_id]
                }
            }
        };
        post('ri/map-hover', hoverPayload)
            .then(response => {
                // Response is array, find matching country_id
                if (Array.isArray(response)) {
                    const data = response.find(d => d.country_id === properties.country_id);
                    setHoverData(prev => ({ ...prev, [properties.country_id]: data }));
                }
            })
            .catch(err => {
                setHoverError(err);
            })
            .finally(() => setHoverLoading(false));
    };

    return (
        <Wrapper>
            <div className="dashboard-container">
                {/* LEFT: Map (only visible if showMapPanel) */}
                <div
                    className="dashboard-leftside"
                    style={
                        showMapPanel
                            ? { flexBasis: 400, minWidth: 320, maxWidth: 440, transition: 'flex-basis 0.8s cubic-bezier(0.4,0,0.2,1)' }
                            : { flexBasis: 0, minWidth: 0, maxWidth: 0, transition: 'flex-basis 0.8s cubic-bezier(0.4,0,0.2,1)', overflow: 'hidden', padding: 0 }
                    }
                >
                    {showMapPanel && (
                        <div
                            className="dashboard-left map-panel-ubuntu map-panel-custom"

                        >
                            {/* Header */}
                            <div className="map-panel-header">
                                <span className="map-panel-title">UAE</span>
                                <button
                                    onClick={() => setShowMapPanel(false)}
                                    className="map-panel-close-btn"
                                    aria-label="Close Map"
                                >
                                    ×
                                </button>
                            </div>
                            {/* Map */}
                            <div className="map-panel-map-container">
                                {(mapLoading || hoverLoading) && (
                                    <div className="map-panel-loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 240 }}>
                                        <img
                                            src={require('../../../assets/images/gcc-map-loader.png')}
                                            alt="Loading map..."
                                            style={{ width: 240, height: 'auto', objectFit: 'contain' }}
                                        />
                                    </div>
                                )}
                                {(mapError || hoverError) && (
                                    <div className="map-panel-error">Error loading map data</div>
                                )}
                                {!mapLoading && !hoverLoading && mapGeoJson && (
                                    <MapComponent
                                        geoJson={mapGeoJson}
                                        mapLevel={1}
                                        northPoint={["31.273904, 34.525594"]}
                                        southPoint={["60.695581, 13.951499"]}
                                        centerPoint={["44.556137,23.785713"]}
                                        zoomLevel={2.5}
                                        onChange={() => { }}
                                        toolTipValues={["country_name", "country_code"]}
                                        toolTipLabels={["Country Name", "Country Code"]}
                                        growthDegrowth={null}
                                        onMarkerHover={handleMarkerHover}
                                        hoverData={hoverData}
                                    />
                                )}
                            </div>
                            {/* Legend */}
                            <div className="map-panel-legend">
                                <span className="map-panel-legend-item">
                                    <span className="legend-dot green"></span>
                                    Sales Value Growth
                                </span>
                                <span className="map-panel-legend-item">
                                    <span className="legend-dot red"></span>
                                    Sales Value De-Growth
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* RIGHT: Dashboard content */}
                <div
                    className="dashboard-right"
                    style={{
                        flex: 1,
                        minWidth: 0
                    }}
                >
                    <SelectedGlobalFilter />
                    <div className="content">
                        <div className="content_section">
                            <PageHeader pageTitle={"Summary"} />
                            <div className="page_content flex column gap_1rem">
                                <SalesPerformance />
                                <DistributionPerformance />
                                <SalesDeepDiveWithWaterfall />
                                <NestedSummaryTables />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </Wrapper>
    )
}

export default Summary
